import { useEffect } from 'react';
import { useAuthStore } from './store';
import { useNavigate } from 'react-router-dom';
import { createSecureToken, verifySecureToken } from './encryption';

// Provide default values for timeouts
const SESSION_TIMEOUT = parseInt(import.meta.env.VITE_SESSION_TIMEOUT || '900000', 10); // 15 minutes
const IDLE_TIMEOUT = parseInt(import.meta.env.VITE_IDLE_TIMEOUT || '300000', 10); // 5 minutes

let idleTimer: number | undefined;
let sessionTimer: number | undefined;
let sessionToken: string | null = null;

export const useSessionManager = () => {
  const { signOut } = useAuthStore();
  const navigate = useNavigate();

  const validateSession = async () => {
    try {
      if (!sessionToken) {
        await signOut();
        navigate('/login');
        return false;
      }

      const payload = await verifySecureToken(sessionToken);
      if (!payload) {
        await signOut();
        navigate('/login');
        return false;
      }
      return true;
    } catch (error) {
      await signOut();
      navigate('/login');
      return false;
    }
  };

  const resetTimers = async () => {
    if (idleTimer) window.clearTimeout(idleTimer);
    if (sessionTimer) window.clearTimeout(sessionTimer);

    try {
      // Create new session token
      sessionToken = await createSecureToken({ timestamp: Date.now() });

      // Set idle timeout
      idleTimer = window.setTimeout(async () => {
        await signOut();
        navigate('/login');
      }, IDLE_TIMEOUT);

      // Set session timeout
      sessionTimer = window.setTimeout(async () => {
        await signOut();
        navigate('/login');
      }, SESSION_TIMEOUT);
    } catch (error) {
      console.error('Error resetting session timers:', error);
      await signOut();
      navigate('/login');
    }
  };

  useEffect(() => {
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    
    const handleActivity = async () => {
      await validateSession();
      await resetTimers();
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Initialize session
    resetTimers();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (idleTimer) window.clearTimeout(idleTimer);
      if (sessionTimer) window.clearTimeout(sessionTimer);
      sessionToken = null;
    };
  }, [signOut, navigate]);

  return { validateSession };
};